// =============================================================================
// AbsenceReasonForm - Form creazione/modifica di una causale di assenza
// =============================================================================

import { useState } from 'react';
import { Loader2, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useCreateAbsenceReason,
  useUpdateAbsenceReason,
  AbsenceReason,
} from '../../api/hooks';
import {
  ALLOWED_ICON_NAMES,
  resolveIcon,
  hexToRgba,
} from '../scheduler/utils/exceptionStyle';

// Palette suggerita per le causali (coerente con i default di sistema).
const COLOR_PRESETS = [
  '#df4e2a',
  '#B45309',
  '#7C3AED',
  '#52525B',
  '#0F766E',
  '#2563EB',
  '#DC2626',
  '#DB2777',
  '#0891B2',
  '#65A30D',
];

interface AbsenceReasonFormProps {
  reason: AbsenceReason | null;
  onClose: () => void;
}

export function AbsenceReasonForm({ reason, onClose }: AbsenceReasonFormProps) {
  const { t } = useTranslation('settings');
  const createReason = useCreateAbsenceReason();
  const updateReason = useUpdateAbsenceReason();
  const isEditing = !!reason;

  const [name, setName] = useState(reason?.name ?? '');
  const [color, setColor] = useState(reason?.color ?? COLOR_PRESETS[0]);
  const [icon, setIcon] = useState(reason?.icon ?? 'CalendarOff');
  const [error, setError] = useState<string | null>(null);

  const isSaving = createReason.isPending || updateReason.isPending;

  const handleSubmit = async () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('absenceReasons.errors.nameRequired'));
      return;
    }
    try {
      if (isEditing && reason) {
        await updateReason.mutateAsync({ id: reason.id, name: trimmed, color, icon });
      } else {
        await createReason.mutateAsync({ name: trimmed, color, icon });
      }
      onClose();
    } catch (e) {
      console.error('Error saving absence reason:', e);
      setError(t('absenceReasons.errors.saveFailed'));
    }
  };

  const PreviewIcon = resolveIcon(icon);

  return (
    <div className="space-y-4">
      <h4 className="text-base font-semibold text-gray-900">
        {isEditing ? t('absenceReasons.editTitle') : t('absenceReasons.createTitle')}
      </h4>

      {/* Anteprima chip */}
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm font-medium"
          style={{ backgroundColor: hexToRgba(color, 0.12), color }}
        >
          <PreviewIcon className="w-4 h-4" />
          {name.trim() || t('absenceReasons.previewPlaceholder')}
        </span>
      </div>

      {/* Nome */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('absenceReasons.nameLabel')}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('absenceReasons.namePlaceholder')}
          maxLength={100}
          className="w-full px-3 py-2 input"
        />
      </div>

      {/* Colore */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('absenceReasons.colorLabel')}
        </label>
        <div className="flex items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`w-7 h-7 rounded-full transition-transform ${
                  color.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                }`}
                style={{ backgroundColor: c }}
                aria-label={c}
              />
            ))}
          </div>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer border border-gray-200"
          />
        </div>
      </div>

      {/* Icona */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('absenceReasons.iconLabel')}
        </label>
        <div className="grid grid-cols-8 gap-2 sm:grid-cols-10">
          {ALLOWED_ICON_NAMES.map((iconName) => {
            const Icon = resolveIcon(iconName);
            const selected = icon === iconName;
            return (
              <button
                key={iconName}
                type="button"
                onClick={() => setIcon(iconName)}
                className={`flex items-center justify-center aspect-square rounded-lg border transition-colors ${
                  selected ? 'ring-2' : 'border-gray-200 hover:bg-gray-50 text-gray-500'
                }`}
                style={selected ? { borderColor: color, color, backgroundColor: hexToRgba(color, 0.08) } : undefined}
                aria-label={iconName}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <X className="w-4 h-4" />
          {t('common:buttons.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSaving}
          className="btn-primary flex items-center gap-2 px-4 py-2 text-sm"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {isEditing ? t('common:buttons.saveChanges') : t('absenceReasons.add')}
        </button>
      </div>
    </div>
  );
}
